import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { emailService } from "@/services/emailService";
import {
  emailTemplateService,
  getDefaultBccEmail,
} from "@/services/emailTemplateService";
import { reportService } from "@/services/reportService";
import {
  EmailTemplate,
  WeeklyReport,
  EmailAttachment,
  EmailDeliveryStatus,
} from "@/types/WeeklyReport";
import { FileAttachment } from "@/components/FileAttachment";
import { EmailDeliveryStatusCard } from "@/components/EmailDeliveryStatusCard";
import {
  MultiEmailAutocomplete,
  MultiEmailAutocompleteRef,
} from "@/components/MultiEmailAutocomplete";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { Eye, Loader2, Mail } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { DateRange } from "react-day-picker";
import { useFieldArray, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import * as z from "zod";
import { useAuth } from "../contexts/AuthContext";

// Custom toast functions with colors
const showSuccessToast = (message: string) => {
  toast(message, {
    style: {
      "--toast-bg": "#dcfce7",
      "--toast-color": "#166534",
      "--toast-border": "1px solid #bbf7d0",
    } as React.CSSProperties,
  });
};

const showInfoToast = (message: string) => {
  toast(message, {
    style: {
      "--toast-bg": "#ffffff",
      "--toast-color": "#374151",
      "--toast-border": "1px solid #e5e7eb",
    } as React.CSSProperties,
  });
};

const showErrorToast = (message: string) => {
  toast(message, {
    style: {
      "--toast-bg": "#fef2f2",
      "--toast-color": "#dc2626",
      "--toast-border": "1px solid #fecaca",
    } as React.CSSProperties,
  });
};

// Form schema
const formSchema = z.object({
  to: z.array(z.string()).default([]),
  cc: z.array(z.string()).default([]),
  bcc: z.array(z.string()).default([]),
  subject: z.string().min(1, "Subject is required"),
  dateRange: z.object({
    from: z.date(),
    to: z.date().optional(),
  }),
  last_week_tasks: z
    .array(
      z.object({
        value: z.string(),
        dependency: z.string().optional(),
      }),
    )
    .default([]),
  Unplanned: z
    .array(
      z.object({
        value: z.string(),
        dependency: z.string().optional(),
      }),
    )
    .default([]),
  blockers: z
    .array(
      z.object({
        value: z.string(),
        dependency: z.string().optional(),
      }),
    )
    .default([]),
  next_week_tasks: z
    .array(
      z.object({
        value: z.string(),
        dependency: z.string().optional(),
      }),
    )
    .default([]),
  further_beyond: z
    .array(
      z.object({
        value: z.string(),
        dependency: z.string().optional(),
      }),
    )
    .default([]),
  notes: z.string().optional(),
  greeting: z.string().optional(),
  signature: z.string().optional(),
  clientName: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface EmailFormProps {
  onSubmit: (report: WeeklyReport) => Promise<void>;
  lastReport?: WeeklyReport;
  reports?: WeeklyReport[];
  defaultSubject?: string;
  onPreview?: (reportData: any) => void;
  previewContent?: string;
}

const EmailForm: React.FC<EmailFormProps> = ({
  onSubmit,
  lastReport,
  reports = [],
  defaultSubject,
  onPreview,
  previewContent,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditingSubject, setIsEditingSubject] = useState(
    !!lastReport?.subject,
  );
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [lastAppliedTemplate, setLastAppliedTemplate] = useState<string | null>(
    null,
  );
  const [includeWeeklyEmail, setIncludeWeeklyEmail] = useState(true);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState({
    title: "",
    description: "",
    confirmLabel: "Continue",
  });
  const [pendingReportData, setPendingReportData] = useState<FormValues | null>(
    null,
  );
  const [tokenStatus, setTokenStatus] = useState<
    "valid" | "expired" | "missing" | "unknown"
  >("unknown");
  const [attachments, setAttachments] = useState<EmailAttachment[]>([]);
  const [deliveryStatus, setDeliveryStatus] =
    useState<EmailDeliveryStatus | null>(null);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [showDraftSaved, setShowDraftSaved] = useState(false);
  // Detect if editing a draft vs sent report
  const isEditingDraft =
    lastReport?.status === "draft" ||
    (lastReport?.status === undefined && lastReport?.sentByEmail === false);

  const navigate = useNavigate();
  const { hasGmailAccess, user } = useAuth();
  const formRef = useRef<HTMLFormElement>(null);
  const toFieldRef = useRef<MultiEmailAutocompleteRef>(null);
  const ccFieldRef = useRef<MultiEmailAutocompleteRef>(null);
  const bccFieldRef = useRef<MultiEmailAutocompleteRef>(null);

  useEffect(() => {
    if (!hasGmailAccess) {
      setTokenStatus("missing");
      // Show warning that authentication is required for sending emails
      showInfoToast("Gmail authentication required to send emails.");
      return;
    }

    const checkTokenStatus = async () => {
      try {
        const { gmailService } = await import("@/services/gmailService");

        // Try to get a valid token from the backend
        const token = await gmailService.getValidAccessToken();

        if (token) {
          console.log("EmailForm - Token obtained successfully");
          setTokenStatus("valid");
        } else {
          console.log("EmailForm - No valid token available");
          setTokenStatus("missing");
        }
      } catch (error) {
        console.error("EmailForm - Error checking token status:", error);
        setTokenStatus("unknown");
      }
    };

    checkTokenStatus();
  }, [hasGmailAccess]);

  // Helper function to generate a default signature
  const getDefaultSignature = () => {
    // Use user's name if available, otherwise extract name from email
    if (user?.name) {
      return `BR,\n${user.name}`;
    } else if (user?.email) {
      const nameFromEmail = user.email.split("@")[0];
      // Capitalize the first letter of each word
      const formattedName = nameFromEmail
        .split(".")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
      return `BR,\n${formattedName}`;
    }
    return "BR,\nYour Name";
  };

  // Helper function to check if a similar draft already exists

  // Normalize report data to handle field name mismatches between backend and frontend
  // Backend uses camelCase, frontend uses snake_case/PascalCase
  const normalizeReport = (report: WeeklyReport | undefined) => {
    if (!report) return undefined;

    const normalized = {
      ...report,
      // Map backend camelCase to frontend expected formats
      last_week_tasks:
        report.last_week_tasks || (report as any).lastWeekTasks || [],
      Unplanned: report.Unplanned || (report as any).unplannedTasks || [],
      next_week_tasks:
        report.next_week_tasks || (report as any).nextWeekTasks || [],
      further_beyond:
        report.further_beyond || (report as any).furtherBeyond || [],
    };

    return normalized;
  };

  // Debug: Log component mount/unmount
  useEffect(() => {
    console.log(
      "🟢 EmailForm MOUNTED with lastReport:",
      lastReport?.id || "null",
    );
    return () => {
      console.log(
        "🔴 EmailForm UNMOUNTING - lastReport was:",
        lastReport?.id || "null",
      );
    };
  }, []); // Empty deps = only on mount/unmount

  // Load email templates
  useEffect(() => {
    if (user?.email) {
      const loadTemplates = async () => {
        try {
          const userTemplates = await emailTemplateService.getTemplatesByUser(
            user.email,
          );
          setTemplates(userTemplates);
        } catch (error) {
          console.error("Error loading email templates:", error);
          setTemplates([]);
        }
      };

      loadTemplates();
    }
  }, [user]);

  // Apply template to form
  const applyTemplate = (templateId: string) => {
    const template = templates.find((t) => t.id === templateId);
    if (!template) return;

    // Apply template fields to form
    form.setValue("to", template.to);
    form.setValue("cc", template.cc);
    form.setValue("bcc", template.bcc);

    // Apply greeting and signature if they exist in the template
    if (template.greeting) {
      form.setValue("greeting", template.greeting);
    }

    if (template.signature) {
      form.setValue("signature", template.signature);
    }

    // Always use the template subject, but replace [Date Range] with the actual date range
    let subject = template.subject;
    const currentDateRange = form.getValues("dateRange");

    // Format the date range for insertion into the subject
    let formattedDateRange = "";
    if (currentDateRange && currentDateRange.from) {
      const fromDate = new Date(currentDateRange.from);
      const toDate = currentDateRange.to
        ? new Date(currentDateRange.to)
        : fromDate;
      formattedDateRange = `${format(fromDate, "MMM d")} - ${format(toDate, "MMM d, yyyy")}`;
    }

    // Replace the [Date Range] placeholder if it exists, otherwise append the date range
    if (subject.includes("[Date Range]")) {
      subject = subject.replace("[Date Range]", formattedDateRange);
    } else if (formattedDateRange && !subject.includes(formattedDateRange)) {
      // Only append if the date range isn't already in the subject
      subject = `${subject} - ${formattedDateRange}`;
    }

    form.setValue("subject", subject);

    // Save template information
    form.setValue("clientName", template.name);

    showSuccessToast(`Applied template: ${template.name}`);

    setLastAppliedTemplate(templateId);
  };

  // Find the most recent SENT reports for initialization
  const allSentReports = React.useMemo(() => {
    return reports
      .filter((report) => report.sentByEmail === true)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [reports]);

  // Get the most recent sent report for initial values
  const lastSentReport = allSentReports[0];

  // Improved date range handling
  const defaultDateRange: DateRange = React.useMemo(() => {
    if (lastReport?.dateRange) {
      try {
        const [start, end] = lastReport.dateRange.split(" - ");
        if (start && end) {
          const fromDate = new Date(start);
          const toDate = new Date(end);

          // Validate that dates are valid
          if (!isNaN(fromDate.getTime()) && !isNaN(toDate.getTime())) {
            return {
              from: fromDate,
              to: toDate,
            };
          }
        }
        console.warn(
          "Invalid dateRange format in draft:",
          lastReport.dateRange,
        );
      } catch (error) {
        console.error("Error parsing dateRange:", error);
      }
    }
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay()); // Start from Sunday
    return {
      from: startOfWeek,
      to: new Date(today),
    };
  }, [lastReport?.dateRange]);

  // Normalize report data to handle field name mismatches
  const normalizedReport = normalizeReport(lastReport);

  // Show build time in console for cache verification
  console.log("Build time:", import.meta.env.BUILD_TIME);

  // Debug: Log when form is initialized
  console.log(
    "🟢 EmailForm RENDER - lastReport prop:",
    lastReport
      ? {
          id: lastReport.id,
          subject: lastReport.subject,
          status: lastReport.status,
          sentByEmail: lastReport.sentByEmail,
          hasLastWeekTasks: lastReport.last_week_tasks?.length || 0,
          hasUnplanned: lastReport.Unplanned?.length || 0,
          first_task: lastReport.last_week_tasks?.[0]?.value,
        }
      : "NULL - creating new report",
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: normalizedReport
      ? {
          // Editing existing report - populate with report data
          subject:
            normalizedReport.subject ||
            defaultSubject ||
            "Develeap Weekly Report",
          dateRange: defaultDateRange,
          to: normalizedReport.to || [],
          cc: normalizedReport.cc || [],
          bcc: normalizedReport.bcc || [],
          last_week_tasks: normalizedReport.last_week_tasks?.map((item) => ({
            value: item.value || "",
            dependency: item.dependency || "",
          })) || [{ value: "", dependency: "" }],
          Unplanned: normalizedReport.Unplanned?.map((item) => ({
            value: item.value || "",
            dependency: item.dependency || "",
          })) || [{ value: "", dependency: "" }],
          blockers: normalizedReport.blockers?.map((item) => ({
            value: item.value || "",
            dependency: item.dependency || "",
          })) || [{ value: "", dependency: "" }],
          next_week_tasks: normalizedReport.next_week_tasks?.map((item) => ({
            value: item.value || "",
            dependency: item.dependency || "",
          })) || [{ value: "", dependency: "" }],
          further_beyond: normalizedReport.further_beyond?.map((item) => ({
            value: item.value || "",
            dependency: item.dependency || "",
          })) || [{ value: "", dependency: "" }],
          notes: normalizedReport.notes || "",
          greeting:
            normalizedReport.greeting ||
            "Hi All,\nPlease take a look at the report for this week below.",
          signature: normalizedReport.signature || getDefaultSignature(),
          clientName: normalizedReport.clientName || "",
        }
      : {
          // New report - completely blank form with only defaults
          subject: defaultSubject || "Develeap Weekly Report",
          dateRange: defaultDateRange,
          to: [],
          cc: [],
          bcc: [],
          last_week_tasks: [{ value: "", dependency: "" }],
          Unplanned: [{ value: "", dependency: "" }],
          blockers: [{ value: "", dependency: "" }],
          next_week_tasks: [{ value: "", dependency: "" }],
          further_beyond: [{ value: "", dependency: "" }],
          notes: "",
          greeting:
            "Hi All,\nPlease take a look at the report for this week below.",
          signature: getDefaultSignature(),
          clientName: "",
        },
  });

  useEffect(() => {
    if (lastReport) {
      const normalized = normalizeReport(lastReport);
      if (normalized) {
        form.reset({
          subject:
            normalized.subject || defaultSubject || "Develeap Weekly Report",
          dateRange: defaultDateRange,
          to: normalized.to || [],
          cc: normalized.cc || [],
          bcc: normalized.bcc || [],
          last_week_tasks: normalized.last_week_tasks?.map((item) => ({
            value: item.value || "",
            dependency: item.dependency || "",
          })) || [{ value: "", dependency: "" }],
          Unplanned: normalized.Unplanned?.map((item) => ({
            value: item.value || "",
            dependency: item.dependency || "",
          })) || [{ value: "", dependency: "" }],
          blockers: normalized.blockers?.map((item) => ({
            value: item.value || "",
            dependency: item.dependency || "",
          })) || [{ value: "", dependency: "" }],
          next_week_tasks: normalized.next_week_tasks?.map((item) => ({
            value: item.value || "",
            dependency: item.dependency || "",
          })) || [{ value: "", dependency: "" }],
          further_beyond: normalized.further_beyond?.map((item) => ({
            value: item.value || "",
            dependency: item.dependency || "",
          })) || [{ value: "", dependency: "" }],
          notes: normalized.notes || "",
          greeting:
            normalized.greeting ||
            "Hi All,\nPlease take a look at the report for this week below.",
          signature: normalized.signature || getDefaultSignature(),
          clientName: normalized.clientName || "",
        });
      }
    }
  }, [lastReport, defaultDateRange, defaultSubject, form]);

  const watchedClientName = form.watch("clientName");
  React.useEffect(() => {
    if (watchedClientName && reports.length > 0) {
      // Find the latest sent report for this client
      const clientReports = reports
        .filter(
          (report) =>
            report.sentByEmail === true &&
            report.clientName === watchedClientName,
        )
        .sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
        );

      if (clientReports.length > 0) {
        const latestClientReport = clientReports[0];

        // Update task fields with data from this client's latest report
        if (
          latestClientReport.next_week_tasks &&
          latestClientReport.next_week_tasks.length > 0
        ) {
          form.setValue(
            "last_week_tasks",
            latestClientReport.next_week_tasks.map((item) => ({
              value: item.value || "",
              dependency: item.dependency || "",
            })),
          );
        }

        if (
          latestClientReport.blockers &&
          latestClientReport.blockers.length > 0
        ) {
          form.setValue(
            "blockers",
            latestClientReport.blockers.map((item) => ({
              value: item.value || "",
              dependency: item.dependency || "",
            })),
          );
        }

        if (
          latestClientReport.further_beyond &&
          latestClientReport.further_beyond.length > 0
        ) {
          form.setValue(
            "further_beyond",
            latestClientReport.further_beyond.map((item) => ({
              value: item.value || "",
              dependency: item.dependency || "",
            })),
          );
        }
      }
    }
  }, [watchedClientName, reports, form]);

  // Watch dateRange changes to update subject
  const watchedDateRange = form.watch("dateRange");
  React.useEffect(() => {
    if (watchedDateRange?.from) {
      const formattedDateRange = `${format(watchedDateRange.from, "MMM d")} - ${format(watchedDateRange.to || watchedDateRange.from, "MMM d, yyyy")}`;

      // Get current subject
      const currentSubject = form.getValues("subject");

      // Simple check for specific templates with placeholder
      if (currentSubject && currentSubject.includes("[Date Range]")) {
        // Handle template placeholder
        const updatedSubject = currentSubject.replace(
          "[Date Range]",
          formattedDateRange,
        );
        form.setValue("subject", updatedSubject);
      } else {
        // For everything else, use a more direct approach:

        // First try to replace existing date pattern if present
        const datePattern = /(\w{3} \d{1,2} - \w{3} \d{1,2}, \d{4})/;

        if (currentSubject && datePattern.test(currentSubject)) {
          // Replace existing date pattern
          const updatedSubject = currentSubject.replace(
            datePattern,
            formattedDateRange,
          );
          form.setValue("subject", updatedSubject);
        } else {
          // No existing date pattern or completely new subject
          // Either use the base subject text or default to "Develeap Weekly Report"
          let baseSubject = "Develeap Weekly Report";

          // Try to extract the base part of the subject (before any date or hyphen)
          if (currentSubject) {
            // If it has a hyphen, get just the part before it
            if (currentSubject.includes(" - ")) {
              baseSubject = currentSubject.split(" - ")[0].trim();
            } else {
              // Otherwise use the whole subject as the base
              baseSubject = currentSubject;
            }
          }

          // Set the subject with the base part and new date range
          form.setValue("subject", `${baseSubject} - ${formattedDateRange}`);
        }
      }
    }
  }, [watchedDateRange, form]);

  const {
    fields: lastWeekTasksFields,
    append: appendLastWeekTasks,
    remove: removeLastWeekTasks,
  } = useFieldArray({
    control: form.control,
    name: "last_week_tasks",
  });

  const {
    fields: UnplannedFields,
    append: appendUnplanned,
    remove: removeUnplanned,
  } = useFieldArray({
    control: form.control,
    name: "Unplanned",
  });

  const {
    fields: blockersFields,
    append: appendBlockers,
    remove: removeBlockers,
  } = useFieldArray({
    control: form.control,
    name: "blockers",
  });

  const {
    fields: nextWeekTasksFields,
    append: appendNextWeekTasks,
    remove: removeNextWeekTasks,
  } = useFieldArray({
    control: form.control,
    name: "next_week_tasks",
  });

  const {
    fields: furtherBeyondFields,
    append: appendFurtherBeyond,
    remove: removeFurtherBeyond,
  } = useFieldArray({
    control: form.control,
    name: "further_beyond",
  });

  // Unified function to generate report from form data
  const generateReportFromForm = (formData: FormValues): WeeklyReport => {
    // Process email fields - they now contain raw strings in the first array element
    const processEmails = (emailField: string[]): string[] => {
      if (!emailField || emailField.length === 0) return [];

      // If we have a single item that contains commas, it's a raw input string
      if (emailField.length === 1 && emailField[0].includes(",")) {
        return emailField[0]
          .split(",")
          .map((email) => email.trim())
          .filter(Boolean);
      }

      // Otherwise, process each item
      return emailField.map((email) => email.trim()).filter(Boolean);
    };

    // Process bcc field to ensure default BCC email appears exactly once
    const defaultBcc = getDefaultBccEmail();
    let bccEmails = processEmails(formData.bcc);

    if (includeWeeklyEmail) {
      // Remove any existing instances of default BCC email first
      bccEmails = bccEmails.filter(
        (email) => email.toLowerCase() !== defaultBcc.toLowerCase(),
      );

      // Then add it once
      bccEmails.push(defaultBcc);
    }

    // Helper function to process task arrays (Reverted to original version)
    const processTaskArray = (
      arr: { value: string; dependency?: string }[] | undefined,
    ): { value: string; dependency?: string }[] => {
      if (!arr) return [];
      return arr
        .filter((item) => item.value?.trim()) // Ensure value exists and is not just whitespace
        .map((item): { value: string; dependency?: string } => ({
          value: item.value.trim(),
          dependency: item.dependency?.trim() || undefined,
        }));
    };

    // Explicitly type formData to ensure correct types are passed
    const typedFormData: FormValues = formData;

    return {
      id: lastReport?.id || uuidv4(),
      date: new Date().toISOString(),
      dateRange: `${format(typedFormData.dateRange.from, "MMM d, yyyy")} - ${format(typedFormData.dateRange.to || typedFormData.dateRange.from, "MMM d, yyyy")}`,
      to: processEmails(typedFormData.to),
      cc: processEmails(typedFormData.cc),
      bcc: bccEmails, // Assuming bccEmails is processed correctly earlier
      subject: typedFormData.subject,
      // Include template information if available
      clientName: typedFormData.clientName || lastReport?.clientName,
      // Use helper to process task arrays from typed form data
      last_week_tasks: processTaskArray(
        typedFormData.last_week_tasks as {
          value: string;
          dependency?: string;
        }[],
      ),
      Unplanned: processTaskArray(
        typedFormData.Unplanned as { value: string; dependency?: string }[],
      ),
      blockers: processTaskArray(
        typedFormData.blockers as { value: string; dependency?: string }[],
      ),
      next_week_tasks: processTaskArray(
        typedFormData.next_week_tasks as {
          value: string;
          dependency?: string;
        }[],
      ),
      further_beyond: processTaskArray(
        typedFormData.further_beyond as {
          value: string;
          dependency?: string;
        }[],
      ),
      notes: typedFormData.notes || "",
      greeting: typedFormData.greeting || "",
      signature: typedFormData.signature || "",
      createdBy: user?.email || "anonymous",
      sentByEmail: false, // Will be set later in handleSubmit
      attachments: attachments.length > 0 ? attachments : undefined,
    };
  };

  const handleSaveDraft = async (data: FormValues) => {
    setIsSubmitting(true);
    try {
      const report = generateReportFromForm(data);

      // Prepare as draft
      const draftReport = {
        ...report,
        status: "draft" as const,
        sentByEmail: false,
        updatedAt: new Date().toISOString(),
        lastSavedBy: user?.email || "unknown",
        draftMetadata: {
          ...report.draftMetadata,
          lastAutoSaveAt: new Date().toISOString(),
          hasUnsavedChanges: false,
        },
      };

      const savedDraft = await reportService.saveDraft(draftReport);

      // Update editing report so form refreshes
      if (editingReport) {
        setEditingReport(savedDraft);
      }

      setLastSavedAt(new Date());
      setShowDraftSaved(true);
      showSuccessToast("Draft saved successfully");

      // Hide notification after 3 seconds
      setTimeout(() => setShowDraftSaved(false), 3000);

      console.log("EmailForm - Draft saved:", savedDraft);
    } catch (error) {
      console.error("EmailForm - Error saving draft:", error);
      showErrorToast("Failed to save draft");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Improved form submission
  const handleSubmit = async (data: FormValues) => {
    if (isSubmitting) {
      console.log("EmailForm - Already submitting, ignoring duplicate call");
      return;
    }

    toFieldRef.current?.commitPendingEmail();
    ccFieldRef.current?.commitPendingEmail();
    bccFieldRef.current?.commitPendingEmail();

    try {
      console.log("EmailForm - handleSubmit called");
      console.log("EmailForm - Form data:", data);
      setIsSubmitting(true);
      setDeliveryStatus(null);

      // Create the WeeklyReport object
      const report = generateReportFromForm(data);
      console.log("EmailForm - Generated report:", report);

      // Always send immediately - no draft functionality
      report.sentByEmail = true;

      console.log(
        "EmailForm - Preparing to send email, getting valid Gmail token...",
      );

      // Get the gmailService and try to get a valid token
      // This will try: local cache -> JWT extraction -> refresh -> backend fetch
      const { gmailService } = await import("@/services/gmailService");

      // Get valid token before proceeding (this handles all refresh logic)
      const validToken = await gmailService.getValidAccessToken();
      console.log("EmailForm - Valid token obtained:", !!validToken);

      if (!validToken) {
        const failedStatus: EmailDeliveryStatus = {
          status: "failed",
          sentAt: new Date().toISOString(),
          error:
            "Your Gmail session has expired. Please sign out and sign back in with Google to refresh your credentials.",
        };
        setDeliveryStatus(failedStatus);
        showErrorToast("Gmail session expired - please sign in again");

        // Clear the invalid tokens
        gmailService.clearTokens();
        return;
      }

      console.log(
        "EmailForm - Gmail token is valid, proceeding with email send",
      );

      // Send email and get delivery status
      const status = await emailService.sendEmail(report);
      console.log("EmailForm - Email delivery status:", status);

      // Store the delivery status
      setDeliveryStatus(status);
      report.deliveryStatus = status;

      if (status.status === "sent") {
        report.status = "sent";
        report.sentByEmail = true;
        report.sentAt = status.sentAt || new Date().toISOString();
        showSuccessToast("Email sent ✓");
      } else if (status.status === "failed") {
        report.status = "failed";
        showErrorToast(`Email failed: ${status.error || "Unknown error"}`);
      }

      console.log("EmailForm - Processing email send for report:", {
        id: report.id,
        subject: report.subject,
        sentByEmail: report.sentByEmail,
      });

      // Pass the report to the parent component's onSubmit handler (for saving)
      await onSubmit(report);

      console.log("EmailForm - Form submission completed successfully");
    } catch (error) {
      console.error("EmailForm - Error submitting form:", error);

      // Handle token expiration specifically
      const errorMessage =
        error instanceof Error ? error.message : "An error occurred";

      const failedStatus: EmailDeliveryStatus = {
        status: "failed",
        sentAt: new Date().toISOString(),
        error: errorMessage,
      };
      setDeliveryStatus(failedStatus);

      if (errorMessage.includes("token is invalid or expired")) {
        showErrorToast("Authentication Required");

        // Redirect to authentication page
        if (typeof window !== "undefined" && window.location) {
          setTimeout(() => {
            window.location.href = "/login";
          }, 2000);
        }
      } else {
        showErrorToast(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Add this function to check if there are any recipients
  const hasRecipients = (formData: FormValues) => {
    return (
      (Array.isArray(formData?.to) && formData.to.length > 0) ||
      (Array.isArray(formData?.cc) && formData.cc.length > 0) ||
      (Array.isArray(formData?.bcc) && formData.bcc.length > 0)
    );
  };

  const handlePreviewClick = () => {
    const formData = form.getValues();
    const report = generateReportFromForm(formData);

    if (onPreview) {
      onPreview(report);
    }

    setIsPreviewOpen(true);
  };

  // Function to show confirmation popup
  const confirmSendEmail = (data: FormValues) => {
    // Process the recipients to get an accurate count
    const processEmails = (emailField: string[]): string[] => {
      if (!emailField || emailField.length === 0) return [];

      // If we have a single item that contains commas, it's a raw input string
      if (emailField.length === 1 && emailField[0].includes(",")) {
        return emailField[0]
          .split(",")
          .map((email) => email.trim())
          .filter(Boolean);
      }

      // Otherwise, process each item
      return emailField.map((email) => email.trim()).filter(Boolean);
    };

    const toEmails = processEmails(data.to);
    const ccEmails = processEmails(data.cc);
    const bccEmails = processEmails(data.bcc);

    // Add default BCC email to BCC if includeWeeklyEmail is true
    const defaultBcc = getDefaultBccEmail();
    let totalBcc = [...bccEmails];
    if (includeWeeklyEmail) {
      // Only add if not already present
      if (
        !totalBcc.some(
          (email) => email.toLowerCase() === defaultBcc.toLowerCase(),
        )
      ) {
        totalBcc.push(defaultBcc);
      }
    }

    const totalRecipients = toEmails.length + ccEmails.length + totalBcc.length;

    setPendingReportData(data);
    setDialogMessage({
      title: "Confirm Send Email",
      description: `Are you sure you want to send this email to ${totalRecipients} recipient${totalRecipients !== 1 ? "s" : ""}?`,
      confirmLabel: "Send Email",
    });
    setShowConfirmDialog(true);
  };

  // Function to verify token before sending
  const verifyAndSend = async () => {
    if (!pendingReportData || isSubmitting) return;

    setShowConfirmDialog(false);

    try {
      // handleSubmit now handles everything including email sending and status
      await handleSubmit(pendingReportData);
    } catch (error) {
      console.error("Error sending email:", error);
      const errorMessage =
        error instanceof Error ? error.message : "An error occurred";

      // Set delivery status for error
      const failedStatus: EmailDeliveryStatus = {
        status: "failed",
        sentAt: new Date().toISOString(),
        error: errorMessage,
      };
      setDeliveryStatus(failedStatus);

      if (errorMessage.includes("token is invalid or expired")) {
        // Set callback to redirect to login page
        setTimeout(() => {
          if (typeof window !== "undefined" && window.location) {
            window.location.href = "/login";
          }
        }, 3000);
      }
    }
  };

  // Simplified: Trust that lastReport prop contains properly loaded and normalized data
  // The Dashboard component now handles loading, normalization, and error states

  // Periodic token refresh to extend session while user is active
  React.useEffect(() => {
    if (!hasGmailAccess) {
      // Don't set up refresh interval if no Gmail access
      return;
    }

    console.log(
      "EmailForm - Setting up periodic token refresh (every 5 minutes)",
    );

    const refreshInterval = setInterval(
      async () => {
        try {
          const { gmailService } = await import("@/services/gmailService");
          const refreshed = await gmailService.refreshTokenIfNeeded();

          if (refreshed) {
            console.log(
              "EmailForm - Token refreshed successfully in background",
            );
          } else {
            console.log("EmailForm - Token refresh failed or not needed");
            // Show subtle feedback if refresh consistently fails
            // This might indicate session issues that user should know about
            showInfoToast(
              "Session may need refresh - consider re-authenticating if you experience issues",
            );
          }
        } catch (error) {
          console.error(
            "EmailForm - Error during periodic token refresh:",
            error,
          );
        }
      },
      5 * 60 * 1000,
    ); // Refresh every 5 minutes

    // Cleanup interval on unmount
    return () => {
      console.log("EmailForm - Clearing periodic token refresh interval");
      clearInterval(refreshInterval);
    };
  }, [hasGmailAccess]);

  return (
    <>
      <Form {...form}>
        <form
          ref={formRef}
          onSubmit={form.handleSubmit(handleSubmit)}
          className="space-y-4"
        >
          <Card>
            <CardContent className="p-4">
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="subject"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Subject</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex flex-col space-y-2">
                  <Label>Email Template</Label>
                  <div className="flex gap-2">
                    <Select onValueChange={applyTemplate}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select a template" />
                      </SelectTrigger>
                      <SelectContent>
                        {templates.length === 0 ? (
                          <SelectItem value="none" disabled>
                            No templates available
                          </SelectItem>
                        ) : (
                          templates.map((template) => (
                            <SelectItem key={template.id} value={template.id}>
                              {template.name}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate("/email-templates")}
                    >
                      Manage Templates
                    </Button>
                  </div>
                  {form.getValues("clientName") && (
                    <div className="flex items-center space-x-2 mt-1">
                      <span className="text-xs font-medium px-2 py-1 rounded-full bg-blue-100 text-blue-800">
                        Client: {form.getValues("clientName")}
                      </span>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Templates will automatically include {getDefaultBccEmail()}{" "}
                    in BCC
                  </p>
                </div>

                <FormField
                  control={form.control}
                  name="dateRange"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Date Range</FormLabel>
                      <FormControl>
                        <DateRangePicker
                          value={field.value as DateRange}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="to"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>To</FormLabel>
                      <FormControl>
                        <MultiEmailAutocomplete
                          ref={toFieldRef}
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Type to search contacts..."
                          maxEmails={1}
                          helperText="Limited to 1 recipient. Use CC for additional recipients."
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="cc"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>CC</FormLabel>
                      <FormControl>
                        <MultiEmailAutocomplete
                          ref={ccFieldRef}
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Type to search contacts..."
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="bcc"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>BCC</FormLabel>
                      <FormControl>
                        <MultiEmailAutocomplete
                          ref={bccFieldRef}
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Type to search contacts..."
                        />
                      </FormControl>
                      <div className="flex items-center space-x-2 mt-1">
                        <Checkbox
                          id="includeWeeklyEmail"
                          checked={includeWeeklyEmail}
                          onCheckedChange={(checked) => {
                            setIncludeWeeklyEmail(checked === true);
                          }}
                        />
                        <label
                          htmlFor="includeWeeklyEmail"
                          className="text-xs text-muted-foreground cursor-pointer"
                        >
                          Include {getDefaultBccEmail()} in BCC when sent
                        </label>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="greeting"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Greeting</FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            placeholder="Hi All,&#10;Please take a look at the report for this week below."
                          />
                        </FormControl>
                        <FormDescription className="text-xs text-muted-foreground">
                          This text will appear above the report sections.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div>
                    <Label>Last Week Tasks</Label>
                    {lastWeekTasksFields.map((field, index) => (
                      <div key={field.id} className="flex gap-2 mb-2">
                        <Input
                          {...form.register(`last_week_tasks.${index}.value`)}
                          placeholder="Task description"
                          className="flex-grow"
                        />
                        <Input
                          {...form.register(
                            `last_week_tasks.${index}.dependency`,
                          )}
                          placeholder="Dependency (optional)"
                          className="w-1/3"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeLastWeekTasks(index)}
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        appendLastWeekTasks({ value: "", dependency: "" })
                      }
                    >
                      Add Last Week Task
                    </Button>
                  </div>

                  <div>
                    <Label>Unplanned</Label>
                    {UnplannedFields.map((field, index) => (
                      <div key={field.id} className="flex gap-2 mb-2">
                        <Input
                          {...form.register(`Unplanned.${index}.value`)}
                          placeholder="Task description"
                          className="flex-grow"
                        />
                        <Input
                          {...form.register(`Unplanned.${index}.dependency`)}
                          placeholder="Dependency (optional)"
                          className="w-1/3"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeUnplanned(index)}
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        appendUnplanned({ value: "", dependency: "" })
                      }
                    >
                      Add Unplanned Item
                    </Button>
                  </div>

                  <div>
                    <Label>Blockers</Label>
                    {blockersFields.map((field, index) => (
                      <div key={field.id} className="flex gap-2 mb-2">
                        <Input
                          {...form.register(`blockers.${index}.value`)}
                          placeholder="Task description"
                          className="flex-grow"
                        />
                        <Input
                          {...form.register(`blockers.${index}.dependency`)}
                          placeholder="Dependency (optional)"
                          className="w-1/3"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeBlockers(index)}
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        appendBlockers({ value: "", dependency: "" })
                      }
                    >
                      Add Blocker
                    </Button>
                  </div>

                  <div>
                    <Label>Next Week Tasks</Label>
                    {nextWeekTasksFields.map((field, index) => (
                      <div key={field.id} className="flex gap-2 mb-2">
                        <Input
                          {...form.register(`next_week_tasks.${index}.value`)}
                          placeholder="Task description"
                          className="flex-grow"
                        />
                        <Input
                          {...form.register(
                            `next_week_tasks.${index}.dependency`,
                          )}
                          placeholder="Dependency (optional)"
                          className="w-1/3"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeNextWeekTasks(index)}
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        appendNextWeekTasks({ value: "", dependency: "" })
                      }
                    >
                      Add Next Week Task
                    </Button>
                  </div>

                  <div>
                    <Label>Further Beyond</Label>
                    {furtherBeyondFields.map((field, index) => (
                      <div key={field.id} className="flex gap-2 mb-2">
                        <Input
                          {...form.register(`further_beyond.${index}.value`)}
                          placeholder="Task description"
                          className="flex-grow"
                        />
                        <Input
                          {...form.register(
                            `further_beyond.${index}.dependency`,
                          )}
                          placeholder="Dependency (optional)"
                          className="w-1/3"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeFurtherBeyond(index)}
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        appendFurtherBeyond({ value: "", dependency: "" })
                      }
                    >
                      Add Further Beyond Task
                    </Button>
                  </div>
                </div>

                <FormField
                  control={form.control}
                  name="notes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Notes</FormLabel>
                      <FormControl>
                        <Textarea {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="signature"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Signature</FormLabel>
                      <FormControl>
                        <Textarea {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* File Attachments */}
                <div className="space-y-2">
                  <Label>Attachments</Label>
                  <FileAttachment
                    attachments={attachments}
                    onAttachmentsChange={setAttachments}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end space-x-4 mt-8">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/")}
            >
              Cancel
            </Button>

            <Button
              type="button"
              variant="outline"
              onClick={handlePreviewClick}
            >
              <Eye className="mr-2 h-4 w-4" />
              Preview
            </Button>

            <Button
              type="button"
              onClick={() => confirmSendEmail(form.getValues())}
              disabled={isSubmitting || !hasGmailAccess}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="mr-2 h-4 w-4" />
                  Send Email
                </>
              )}
            </Button>
          </div>

          {/* Email Delivery Status Card */}
          {deliveryStatus && (
            <EmailDeliveryStatusCard
              status={deliveryStatus}
              onRetry={() => {
                setDeliveryStatus(null);
                confirmSendEmail(form.getValues());
              }}
              onDismiss={() => setDeliveryStatus(null)}
            />
          )}

          {/* Token status notification */}
          {!hasGmailAccess && (
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-md text-amber-800 text-sm">
              <div className="flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="font-semibold">Gmail Access Required</span>
              </div>
              <p className="mt-1 ml-7">
                The "Send Email" button is disabled because you don't have Gmail
                access or your token has expired. Please{" "}
                <a
                  href="/login"
                  className="underline font-medium hover:text-amber-900"
                >
                  sign in with Google
                </a>{" "}
                to enable email sending.
              </p>
            </div>
          )}

          {/* Token expired notification */}
          {hasGmailAccess && tokenStatus === "expired" && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md text-red-800 text-sm">
              <div className="flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="font-semibold">Gmail Token Expired</span>
              </div>
              <p className="mt-1 ml-7">
                Your Gmail authorization has expired. The system attempted to
                refresh your token but was unsuccessful. Please{" "}
                <a
                  href="/login"
                  className="underline font-medium hover:text-red-900"
                >
                  sign in with Google again
                </a>{" "}
                to renew your Gmail access.
              </p>
            </div>
          )}
        </form>
      </Form>

      {/* Email Preview Dialog */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Email Preview</DialogTitle>
            <DialogDescription>
              Preview how your weekly report email will appear to recipients.
            </DialogDescription>
          </DialogHeader>

          {previewContent ? (
            <div className="mt-4 border rounded-md p-4 bg-white">
              <div dangerouslySetInnerHTML={{ __html: previewContent }} />
            </div>
          ) : (
            <div className="py-4 text-center text-gray-500">
              No preview available. Please fill in the report form and try
              again.
            </div>
          )}

          <div className="flex justify-end mt-4">
            <Button type="button" onClick={() => setIsPreviewOpen(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{dialogMessage.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {dialogMessage.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={verifyAndSend}>
              {dialogMessage.confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default EmailForm;
